import { Check } from '@phosphor-icons/react';
import { includesMember } from '@shh/shared';
import { cn, Heading, Icon, personSolid } from '@shh/ui';
import { Button as AriaButton } from 'react-aria-components';
import { Link } from 'react-router';
import { weekDays } from '../../data/agenda';
import { toggleTodo } from '../../data/api';
import { visibleTodo } from '../../data/derive';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';

/*
 * Small lists beside or under the calendar (Mike, 19.10.: "Das darf da nur eine ganz kompakte
 * Liste sein … das Design mit den runden Hintergrundflächen ist dafür nicht geeignet"). One line
 * per entry, long titles cut off – they never grow into bubbles.
 */

const row =
  'flex min-h-touch-min w-full cursor-pointer items-center gap-gap-sm rounded-control px-inset-xs text-left outline-none data-hovered:bg-action-quiet-hover data-focus-visible:outline-3 data-focus-visible:outline-border-focus';

/** The next seven days, one line per event. */
export function NextDays({
  f,
  lens,
  onOpen,
  className,
}: {
  f: FamilyData;
  lens: string;
  onOpen: (id: string, date: string) => void;
  className?: string;
}) {
  const days = weekDays(f, 1, 7, lens);
  return (
    <section aria-labelledby="next-seven" className={cn('flex flex-col gap-gap-sm', className)}>
      <Heading level={2} size="md" id="next-seven">
        {calendarText.nextSeven}
      </Heading>
      <ol className="flex flex-col">
        {days.map((d) => (
          <li key={d.key} className="flex flex-col border-t border-border-subtle py-gap-xs">
            <span
              className={cn(
                'px-inset-xs text-sm font-black',
                d.isWeekend ? 'text-accent-secondary' : 'text-text-secondary',
              )}
            >
              {d.label} · {d.date}
            </span>
            {d.items.length ? (
              d.items.map((i) => {
                const people = i.people === 'family' ? [] : i.people;
                return (
                  <AriaButton
                    key={i.id}
                    onPress={() => onOpen(i.id, d.key)}
                    aria-label={`${d.label} ${d.date}, ${i.time ?? calendarText.allDay}, ${i.title}`}
                    className={row}
                  >
                    <span className="w-16 shrink-0 text-sm font-bold text-text-secondary tabular-nums">
                      {i.time ?? calendarText.allDay}
                    </span>
                    <span
                      aria-hidden
                      className={cn(
                        'size-2.5 shrink-0 rounded-avatar',
                        people[0] ? personSolid[people[0].color] : 'bg-accent-secondary',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate font-semibold">{i.title}</span>
                  </AriaButton>
                );
              })
            ) : (
              <span className="px-inset-xs text-sm text-text-muted">{calendarText.nothingYet}</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Today's to-dos – only the real to-dos, not the routines with stars (Mike, 19.10.). */
export function TodayTodos({
  f,
  people,
  className,
}: {
  f: FamilyData;
  people?: string[];
  className?: string;
}) {
  const todos = f.todos
    .filter(
      (t) => t.assignees === 'all' || !people || people.some((p) => includesMember(t.assignees, p)),
    )
    // open first; done ones stay until the day is over, like on Heute
    .filter((t) => visibleTodo(f, t))
    .sort((a, b) => Number(a.done) - Number(b.done));
  const shown = todos.slice(0, 6);
  return (
    <section aria-labelledby="todo-today" className={cn('flex flex-col gap-gap-sm', className)}>
      <div className="flex items-baseline justify-between gap-gap-sm">
        <Heading level={2} size="md" id="todo-today">
          {calendarText.todayTodo}
        </Heading>
        <Link to="/aufgaben" className="text-sm font-bold text-text-secondary underline">
          {calendarText.allTasks}
        </Link>
      </div>
      {shown.length ? (
        <ul className="flex flex-col">
          {shown.map((t) => {
            const who =
              t.assignees === 'all'
                ? undefined
                : f.members.find((m) => includesMember(t.assignees, m.id));
            return (
              <li key={t.id}>
                <AriaButton
                  aria-pressed={t.done}
                  onPress={() => void toggleTodo(f.householdId, t, f.meId)}
                  className={row}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid size-6 shrink-0 place-items-center rounded-control border-2',
                      t.done
                        ? 'border-action-primary bg-action-primary text-text-on-action'
                        : 'border-border-default bg-surface-raised',
                    )}
                  >
                    {t.done && <Icon icon={Check} size="sm" weight="bold" />}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate font-semibold',
                      t.done && 'text-text-muted line-through',
                    )}
                  >
                    {t.title}
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      'size-2.5 shrink-0 rounded-avatar',
                      who ? personSolid[who.color] : 'bg-accent-secondary',
                    )}
                  />
                </AriaButton>
              </li>
            );
          })}
          {todos.length > shown.length && (
            <li className="px-inset-xs pt-gap-xs text-sm font-bold text-text-secondary">
              <Link to="/aufgaben" className="underline">
                {calendarText.moreTodos(todos.length - shown.length)}
              </Link>
            </li>
          )}
        </ul>
      ) : (
        <span className="px-inset-xs text-sm text-text-muted">{calendarText.noTodos}</span>
      )}
    </section>
  );
}
